import { useCallback, useMemo } from 'react';
import { lowerFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { type ContactFormValues } from './AddEditContact/useContactForm';
import { type PhoneNumber } from '~/common/types';
import { type PhoneNumberData } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.types';

import { ContactDuplicateReasonType, LogicalOperator } from '~/common/enums';
import { type RouterInputs, type RouterOutputs, trpc } from '@/config/trpc';
import { TYPES } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.data';

import { InvalidIcon, WarningIcon } from '@/shared/icons/Icons';

type DuplicateAlertInfo = {
  color: 'danger' | 'warning';
  contactId: string;
  label: string;
  Icon: React.ElementType;
};

type DuplicatePredicate = {
  predicate: (duplicateContact: QueryContact, queryInput: QueryInput) => boolean;
  reason: ContactDuplicateReasonType;
};

type QueryContact = RouterOutputs['contact']['list'][number];

type QueryInput = NonNullable<Exclude<RouterInputs['contact']['list'], void>>;

const DEFAULT_QUERY_DATA = {
  emailAddresses: [],
  firstName: null,
  lastName: null,
  logicalOperator: LogicalOperator.And,
  mobilePhoneNumber: null,
  phoneNumber: null,
};

const getPhoneNumbers = (phoneNumbers: PhoneNumberData[]) => {
  let defaultLandlineNumber;
  let defaultMobileNumber;

  for (let i = 0; i < phoneNumbers.length; i += 1) {
    const phoneNumber = phoneNumbers[i];

    if (phoneNumber.isDefault) {
      if (phoneNumber.type === TYPES.LANDLINE) defaultLandlineNumber = phoneNumber;
      else defaultMobileNumber = phoneNumber;
    }

    if (defaultLandlineNumber && defaultMobileNumber) break;
  }

  return {
    landline: {
      countryCode: defaultLandlineNumber?.countryCode ?? null,
      number: defaultLandlineNumber?.number ?? null,
    },
    mobile: {
      countryCode: defaultMobileNumber?.countryCode ?? null,
      number: defaultMobileNumber?.number ?? null,
    },
  };
};

const isFilledInPhoneNumber = (phoneNumber: PhoneNumber) =>
  !!phoneNumber && !!phoneNumber.countryCode && !!phoneNumber.number;

const stringMatch = (a: string | null | undefined, b: string | null | undefined) => {
  const aValue = a ?? '';
  const bValue = b ?? '';

  return (
    aValue.localeCompare(bValue, undefined, {
      sensitivity: 'accent',
    }) === 0
  );
};

const reasonPredicates: DuplicatePredicate[] = [
  {
    predicate: (duplicateContact: QueryContact, queryInput: QueryInput) =>
      stringMatch(duplicateContact.firstName, queryInput.firstName) &&
      stringMatch(duplicateContact.lastName, queryInput.lastName),
    reason: ContactDuplicateReasonType.Name,
  },
  {
    predicate: (duplicateContact: QueryContact, queryInput: QueryInput) => {
      const inputHasEmail = (email: string) =>
        queryInput.emailAddresses.some((emailAddress) => emailAddress === email);

      return duplicateContact.emailAddresses.some(inputHasEmail);
    },
    reason: ContactDuplicateReasonType.Email,
  },
  {
    predicate: (duplicateContact: QueryContact, queryInput: QueryInput) =>
      !!queryInput.phoneNumber?.number &&
      duplicateContact.phoneNumber?.countryCode === queryInput.phoneNumber?.countryCode &&
      duplicateContact.phoneNumber?.number === queryInput.phoneNumber?.number,
    reason: ContactDuplicateReasonType.PhoneNumber,
  },
  {
    predicate: (duplicateContact: QueryContact, queryInput: QueryInput) =>
      !!queryInput.mobilePhoneNumber?.number &&
      duplicateContact.mobilePhoneNumber?.countryCode ===
        queryInput.mobilePhoneNumber?.countryCode &&
      duplicateContact.mobilePhoneNumber?.number === queryInput.mobilePhoneNumber?.number,
    reason: ContactDuplicateReasonType.MobilePhoneNumber,
  },
];

const getDuplicateReason = (duplicateContact: QueryContact, queryInput: QueryInput) => {
  for (let i = 0; i < reasonPredicates.length; i += 1) {
    const predicate = reasonPredicates[i];

    if (predicate.predicate(duplicateContact, queryInput)) {
      return predicate.reason;
    }
  }

  return null;
};

const useDuplicateCheck = ({
  contactId,
  values,
}: {
  contactId?: string;
  values: ContactFormValues;
}) => {
  const { t } = useTranslation();

  const queryInput = useMemo(() => {
    const emailAddresses =
      values.emailAddresses
        .map((emailAddress) => emailAddress.email)
        .filter((emailString) => emailString) ?? [];
    const phoneNumbers = getPhoneNumbers(values.phoneNumbers);

    if (
      emailAddresses.length === 0 &&
      (!values.firstName || !values.lastName) &&
      !isFilledInPhoneNumber(phoneNumbers.landline) &&
      !isFilledInPhoneNumber(phoneNumbers.mobile)
    )
      return null;

    return {
      emailAddresses,
      firstName: values.firstName,
      lastName: values.lastName,
      logicalOperator: LogicalOperator.Or,
      mobilePhoneNumber: phoneNumbers.mobile,
      phoneNumber: phoneNumbers.landline,
    };
  }, [values.emailAddresses, values.firstName, values.lastName, values.phoneNumbers]);

  const {
    data: duplicateContacts,
    isFetching: isCheckingForDuplicate,
    refetch: refetchDuplicates,
  } = trpc.contact.list.useQuery(queryInput ?? DEFAULT_QUERY_DATA, { enabled: false });

  const duplicateReason = useMemo(() => {
    const duplicateContact = duplicateContacts?.find((contact) => contact.id !== contactId);

    if (!duplicateContact || !queryInput) return null;

    const reason = getDuplicateReason(duplicateContact, queryInput);

    return reason ? { contactId: duplicateContact.id, reason } : null;
  }, [contactId, duplicateContacts, queryInput]);

  const checkForDuplicates = useCallback(() => {
    if (!queryInput) return;

    refetchDuplicates();
  }, [queryInput, refetchDuplicates]);

  const alertInfoProps: DuplicateAlertInfo | null = useMemo(() => {
    if (!duplicateReason) return null;

    const alertLabel = t(`contactDuplicateReasonType.${lowerFirst(duplicateReason.reason)}`);

    switch (duplicateReason?.reason) {
      case ContactDuplicateReasonType.Name:
        return {
          color: 'warning',
          contactId: duplicateReason.contactId,
          Icon: WarningIcon,
          label: alertLabel,
        };

      case ContactDuplicateReasonType.Email:
        return {
          color: 'danger',
          contactId: duplicateReason.contactId,
          Icon: InvalidIcon,
          label: alertLabel,
        };
      case ContactDuplicateReasonType.MobilePhoneNumber:
      case ContactDuplicateReasonType.PhoneNumber:
        return {
          color: 'danger',
          contactId: duplicateReason.contactId,
          Icon: InvalidIcon,
          label: alertLabel,
        };
      default:
        return null;
    }
  }, [duplicateReason, t]);

  const checkMeta = useMemo(
    () => ({
      alertInfoProps,
      isChecking: isCheckingForDuplicate,
      reason: duplicateReason,
    }),
    [alertInfoProps, duplicateReason, isCheckingForDuplicate],
  );

  return [checkForDuplicates, checkMeta] as const;
};

export default useDuplicateCheck;
